import { useState, useEffect } from 'react';
import { getOnboardingStatus } from '../services/onboardingService';

/**
 * useOnboardingStatus
 *
 * Custom hook to fetch the onboarding status for the current user's organization.
 * The organization ID is obtained from the authentication token, so no organizationId
 * parameter is needed.
 * 
 * Handles loading, success, and error states internally.
 *
 * @param {string|null} organizationId - DEPRECATED: Kept for backward compatibility but not used.
 *                                       Organization ID is now obtained from the auth token.
 * @param {{ skipFetch?: boolean }} [options] - skipFetch: true to skip API call when Redux already has onboardingData.
 * @returns {{ data: object|null, loading: boolean, error: Error|null }}
 */
export const useOnboardingStatus = (organizationId, options = {}) => {
  const { skipFetch = false } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (skipFetch) {
      setLoading(false);
      return;
    }

    const organizationIdFromStorage = localStorage.getItem('organizationId');
    if (!organizationId && !organizationIdFromStorage) {
      setData(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    getOnboardingStatus()
      .then((response) => {
        if (!isMounted) return;
        const onboardingData = response?.data || response;
        setData(onboardingData);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [organizationId, skipFetch]);

  return { data, loading, error };
};
